import { useRef, type ReactNode } from 'react';

import { cn } from '@/lib/utils';
import { useOverflowing } from '@/shared/hooks/useOverflowing';

/**
 * A row of filter chips that wraps on a desktop and scrolls sideways on a phone,
 * fading at its edge while more chips are past it (docs/phone.md PH-02, spec
 * 280). Wrapped on a phone, two short groups took three rows of 44px chips
 * before the list began. `FilterChipGroup` scrolls the same way; this is the
 * row for the pages that draw their own pressed chips.
 */
export function ChipRow({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: ReactNode;
}) {
  const row = useRef<HTMLDivElement>(null);
  useOverflowing(row);
  return (
    <div
      ref={row}
      role="group"
      aria-label={label}
      className={cn(
        'overflow-fade scrollbar-none flex flex-wrap gap-1.5 max-md:flex-nowrap max-md:overflow-x-auto max-md:[&>*]:shrink-0',
        className,
      )}
    >
      {children}
    </div>
  );
}
